import { Button, LocationIcon, TickIcon } from 'components/common'
import { DeliveryType, ServiceTime } from 'components/common/types'
import { useAppSelector } from 'components/hooks/redux-selectors'

import React from 'react'
import { checkIsSpecialBusiness } from 'utils/businessHelpers'
import { formatServiceTime } from 'utils/dateHelpers'

type Props = {
  onAddressClick?: () => void
  onDeliveryTypeClick?: (deliveryType: DeliveryType) => void
  onServiceTimeClick?: (serviceType: ServiceTime) => void
  businessTypeId: number
}

const ServiceDeliveryOptions = ({
  onAddressClick = () => {},
  onDeliveryTypeClick = () => {},
  onServiceTimeClick = () => {},
  businessTypeId
}: Props) => {
  const { destinationAddress, deliveryType, serviceTime, deliveryDate } =
    useAppSelector((state) => state.serviceDeliveryOptions)
  const {
    profileInfo: { isLoading: isProfileLoading },
    isAuthenticated
  } = useAppSelector((state) => state.auth)

  const handleDeliveryTypeClick = (deliveryType: DeliveryType) => {
    onDeliveryTypeClick(deliveryType)
  }

  const handleAddressClick = () => {
    onAddressClick()
  }

  const handleServiceTimeClick = (serviceType: ServiceTime) => {
    onServiceTimeClick(serviceType)
  }

  const fullAddress = destinationAddress.address
  const isSpecialBusiness = checkIsSpecialBusiness(businessTypeId)

  const isPickUp = deliveryType === DeliveryType.PICK_UP
  const isDeliver = deliveryType === DeliveryType.DELIVER
  return (
    <div
      id="service-delivery-options"
      className={`flex items-center gap-x-2 gap-y-4 xl:flex-row justify-center w-full ${
        isSpecialBusiness ? '' : 'opacity-50 pointer-events-none'
      }`}
    >
      <div className="flex flex-col xl:flex-row items-center gap-x-2 rounded-[25px] xl:rounded-[500px] bg-zinc-100 p-2">
        <OptionButton
          text="Delivery"
          isActive={isSpecialBusiness ? isDeliver : null}
          onClick={() => handleDeliveryTypeClick(DeliveryType.DELIVER)}
        />
        <OptionButton
          isActive={isSpecialBusiness ? isPickUp : null}
          text="Pickup"
          onClick={() => handleDeliveryTypeClick(DeliveryType.PICK_UP)}
        />
      </div>

      {isProfileLoading && isAuthenticated ? (
        <div className="skeleton h-[50px] min-w-[150px] flex-1 xl:flex-none rounded-[2.5rem]"></div>
      ) : (
        <>
          {isPickUp && (
            <Address text={isSpecialBusiness ? 'Pick up at business' : ''} />
          )}

          {isDeliver && (
            <Address
              onClick={handleAddressClick}
              className={'cursor-pointer'}
              text={isSpecialBusiness ? fullAddress || 'Select Address' : ''}
            />
          )}
        </>
      )}

      <div className="flex flex-col xl:flex-row items-center gap-x-2 rounded-[25px] xl:rounded-[500px] bg-zinc-100 p-2">
        <OptionButton
          text="ASAP"
          isActive={isSpecialBusiness ? serviceTime === ServiceTime.ASAP : null}
          onClick={() => handleServiceTimeClick(ServiceTime.ASAP)}
        />
        <OptionButton
          text={
            <div className="max-w-[60px] xl:max-w-none">
              Later
              {serviceTime === ServiceTime.LATER && (
                <div className="truncate ">
                  {formatServiceTime(new Date(deliveryDate))}
                </div>
              )}
            </div>
          }
          isActive={
            isSpecialBusiness ? serviceTime === ServiceTime.LATER : null
          }
          onClick={() => handleServiceTimeClick(ServiceTime.LATER)}
        />
      </div>
    </div>
  )
}

const Address = ({
  text,
  onClick = () => {},
  className
}: {
  text: string
  onClick?: () => void
  className?: string
}) => (
  <div
    className={`mt-0 flex min-h-[50px] lg:max-w-[300px] flex-1 ${className} items-center rounded-[2.5rem] flex-1 font-bold  bg-zinc-100 px-6 py-2 xl:mx-0 w-full h-full overflow-hidden`}
    onClick={onClick}
  >
    <LocationIcon className="mr-2 min-h-[16px] min-w-[16px] fill-green" />

    <span className="text-2xl truncate">{text}</span>
  </div>
)

const OptionButton = ({ text, isActive, ...props }) => (
  <Button
    {...props}
    text={
      <div className="flex items-center max-w-[70px] xl:max-w-none">
        <span className={`${isActive ? '' : ''} `}>{text}</span>
        {isActive && <TickIcon className="min-w-[12px] min-h-[12px] ml-2" />}
      </div>
    }
    className={`mt-0 min-w-min rounded-[25px] xl:rounded-[500px] bg-transparent font-bold text-black w-[94px] ${
      isActive
        ? '!bg-white  border-2 border-green border-solid'
        : '!bg-transparent'
    }`}
  />
)

export default ServiceDeliveryOptions
